import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Camera, Upload, Check, ImageIcon, ChevronLeft, AlertTriangle, Download, EyeOff, X, DownloadCloud, Clock, Calendar, ChevronRight, Copy, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to calculate days remaining
const calculateDaysRemaining = (expirationDate) => {
  if (!expirationDate) return null;
  const expDate = new Date(expirationDate + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = expDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Helper function to format date in local format
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString();
};

// Check if device is mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

export default function UploadPage() {
  const { code } = useParams();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showInfoBanners, setShowInfoBanners] = useState(true);
  const [showHideConfirm, setShowHideConfirm] = useState(false);
  const [photoToHide, setPhotoToHide] = useState(null);
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const [lightboxOpacity, setLightboxOpacity] = useState(0);
  const [swipeOffsetY, setSwipeOffsetY] = useState(0);
  const [lockedDirection, setLockedDirection] = useState(null); // 'horizontal' | 'vertical' | null
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);
  const carouselRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchCurrentX = useRef(0);
  const touchCurrentY = useRef(0);
  const isScrollingHorizontally = useRef(false);
  
  // Session ID for massive events - persists across page reloads within same browser session
  const [sessionId] = useState(() => {
    const storageKey = `album_session_${code}`;
    let existingSession = sessionStorage.getItem(storageKey);
    if (!existingSession) {
      existingSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(storageKey, existingSession);
    }
    return existingSession;
  });
  
  // Instagram username for massive events - persists across page reloads
  const [uploaderInstagram, setUploaderInstagram] = useState(() => {
    const storageKey = `album_instagram_${code}`;
    return sessionStorage.getItem(storageKey) || '';
  });
  
  // Save instagram to sessionStorage when it changes
  const handleInstagramChange = (value) => {
    const cleanValue = value.replace('@', '').trim();
    setUploaderInstagram(cleanValue);
    sessionStorage.setItem(`album_instagram_${code}`, cleanValue);
  };

  // Auto-hide info banners after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowInfoBanners(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Block body scroll when lightbox is open
  useEffect(() => {
    if (selectedPhoto) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [selectedPhoto]);

  const fetchAlbum = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/albums/${code}`);
      setAlbum(response.data);
    } catch (error) {
      toast.error(t('albumNotFound'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [code, t]);

  const fetchPhotos = useCallback(async () => {
    try {
      // For massive events, pass session_id to filter photos
      const params = album?.is_massive_event ? `?session_id=${sessionId}` : '';
      const response = await axios.get(`${API}/albums/${code}/photos${params}`);
      const newPhotos = response.data;
      
      // Only update state if photos actually changed (prevents scroll reset)
      setPhotos(prevPhotos => {
        // Compare by checking IDs and length
        if (prevPhotos.length !== newPhotos.length) {
          return newPhotos;
        }
        const prevIds = prevPhotos.map(p => p.id).join(',');
        const newIds = newPhotos.map(p => p.id).join(',');
        if (prevIds !== newIds) {
          return newPhotos;
        }
        return prevPhotos; // No change, keep previous state to avoid re-render
      });
    } catch (error) {
      console.error(error);
    }
  }, [code, album?.is_massive_event, sessionId]);

  useEffect(() => {
    fetchAlbum();
    fetchPhotos();
    
    const interval = setInterval(fetchPhotos, 5000);
    return () => clearInterval(interval);
  }, [fetchAlbum, fetchPhotos]);

  const MAX_FILES_PER_UPLOAD = 10;

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    let imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast.error(t('selectImagesOnly'));
      return;
    }

    // Limit to MAX_FILES_PER_UPLOAD files at a time
    if (imageFiles.length > MAX_FILES_PER_UPLOAD) {
      toast.warning(t('maxFilesWarning') || `Máximo ${MAX_FILES_PER_UPLOAD} fotos por carga. Se subirán las primeras ${MAX_FILES_PER_UPLOAD}.`);
      imageFiles = imageFiles.slice(0, MAX_FILES_PER_UPLOAD);
    }

    // For massive events, show Instagram modal first
    if (album?.is_massive_event) {
      setPendingFiles(imageFiles);
      setShowInstagramModal(true);
      return;
    }

    // For normal albums, upload directly
    await performUpload(imageFiles);
  };

  const performUpload = async (imageFiles) => {
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    imageFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      // Include session_id and uploader_instagram for massive events
      let uploadUrl = `${API}/albums/${code}/photos`;
      if (album?.is_massive_event) {
        const params = new URLSearchParams();
        params.append('session_id', sessionId);
        if (uploaderInstagram) {
          params.append('uploader_instagram', uploaderInstagram);
        }
        uploadUrl = `${uploadUrl}?${params.toString()}`;
      }
      
      await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });
      
      toast.success(`${imageFiles.length} ${imageFiles.length > 1 ? t('photos') : t('photo')} ${t('photosAddedToGallery').toLowerCase()}!`);
      fetchAlbum(); // Refresh album to get updated photo_count
      fetchPhotos();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('albumFull'));
      } else {
        toast.error(t('uploadFailed'));
      }
      console.error(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle Instagram modal submit
  const handleInstagramModalSubmit = () => {
    setShowInstagramModal(false);
    if (pendingFiles.length > 0) {
      performUpload(pendingFiles);
      setPendingFiles([]);
    }
  };

  // Handle Instagram modal cancel
  const handleInstagramModalCancel = () => {
    setShowInstagramModal(false);
    setPendingFiles([]);
  };

  const handleFileSelect = (e) => {
    handleUpload(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Detect if device is mobile for share API
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const handleDownloadPhoto = async (photo) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${photo.url}`);
      const blob = await response.blob();
      // Use the new filename (album_date_time format) for download
      const filename = photo.filename || photo.original_filename || `photo_${photo.id}.jpg`;
      
      // On mobile, try to use Web Share API for direct save to gallery
      if (isMobileDevice() && navigator.share && navigator.canShare) {
        const file = new File([blob], filename, { type: blob.type });
        const shareData = { files: [file] };
        
        if (navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            toast.success(t('photoShared'));
            return;
          } catch (shareError) {
            // User cancelled or share failed, fall back to download
            if (shareError.name !== 'AbortError') {
              console.log('Share failed, falling back to download');
            }
          }
        }
      }
      
      // Fallback: standard download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('failedToDownload'));
    }
  };

  const handleDownloadAll = () => {
    window.location.href = `${API}/albums/${code}/download`;
    toast.success(t('preparingDownload'));
  };

  const handleHidePhoto = async (photoId) => {
    try {
      await axios.patch(`${API}/albums/${code}/photos/${photoId}/hide`);
      setPhotos(photos.filter(p => p.id !== photoId));
      setSelectedPhoto(null);
      setShowHideConfirm(false);
      setPhotoToHide(null);
      toast.success(t('photoHidden'), {
        description: t('photoHiddenMessage'),
        duration: 5000
      });
    } catch (error) {
      toast.error(t('failedToDelete'));
    }
  };

  // Show confirmation modal before hiding
  const confirmHidePhoto = (photo, e) => {
    if (e) e.stopPropagation();
    setPhotoToHide(photo);
    setShowHideConfirm(true);
  };

  // Open photo in lightbox with animation
  const openLightbox = (photo, index) => {
    setSelectedPhoto(photo);
    setSelectedPhotoIndex(index);
    setSwipeOffsetY(0);
    setLightboxVisible(true);
    requestAnimationFrame(() => {
      setLightboxOpacity(1);
      // Scroll to the selected photo after render
      setTimeout(() => {
        if (carouselRef.current) {
          const scrollPosition = index * window.innerWidth;
          carouselRef.current.scrollLeft = scrollPosition;
        }
      }, 50);
    });
  };

  // Close lightbox with animation
  const closeLightbox = () => {
    setLightboxOpacity(0);
    setTimeout(() => {
      setLightboxVisible(false);
      setSelectedPhoto(null);
      setSwipeOffsetY(0);
    }, 200);
  };

  // Handle scroll snap - detect which photo is visible
  const handleCarouselScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const itemWidth = window.innerWidth;
      const newIndex = Math.round(scrollLeft / itemWidth);
      if (newIndex !== selectedPhotoIndex && newIndex >= 0 && newIndex < photos.length) {
        setSelectedPhotoIndex(newIndex);
        setSelectedPhoto(photos[newIndex]);
      }
    }
  };

  // Navigate to specific photo
  const navigatePhoto = (direction) => {
    const newIndex = selectedPhotoIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length && carouselRef.current) {
      const scrollPosition = newIndex * window.innerWidth;
      carouselRef.current.scrollLeft = scrollPosition;
    }
  };

  // Go to specific index
  const goToPhoto = (index) => {
    if (carouselRef.current && index >= 0 && index < photos.length) {
      const scrollPosition = index * window.innerWidth;
      carouselRef.current.scrollLeft = scrollPosition;
    }
  };

  // Directional locking touch handlers
  const handleLightboxTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchCurrentX.current = e.touches[0].clientX;
    touchCurrentY.current = e.touches[0].clientY;
    setLockedDirection(null);
    isScrollingHorizontally.current = false;
  };

  const handleLightboxTouchMove = (e) => {
    touchCurrentX.current = e.touches[0].clientX;
    touchCurrentY.current = e.touches[0].clientY;
    
    const diffX = touchCurrentX.current - touchStartX.current;
    const diffY = touchCurrentY.current - touchStartY.current;
    
    // Determine direction lock on first significant movement (threshold: 10px)
    if (!lockedDirection && (Math.abs(diffX) > 10 || Math.abs(diffY) > 10)) {
      if (Math.abs(diffX) > Math.abs(diffY)) {
        // Lock to horizontal - for photo navigation
        setLockedDirection('horizontal');
        isScrollingHorizontally.current = true;
      } else {
        // Lock to vertical - for closing gesture
        setLockedDirection('vertical');
        isScrollingHorizontally.current = false;
        // Prevent horizontal scroll when swiping vertically
        if (carouselRef.current) {
          carouselRef.current.style.overflowX = 'hidden';
        }
      }
    }
    
    // Apply movement based on locked direction
    if (lockedDirection === 'vertical' && diffY > 0) {
      // Vertical swipe down - close gesture (perfectly straight)
      e.preventDefault();
      setSwipeOffsetY(diffY);
      const newOpacity = Math.max(0.2, 1 - (diffY / 350));
      setLightboxOpacity(newOpacity);
    }
    // Horizontal movement is handled natively by the scroll snap carousel
  };

  const handleLightboxTouchEnd = () => {
    const diffY = touchCurrentY.current - touchStartY.current;
    
    // Re-enable horizontal scroll
    if (carouselRef.current) {
      carouselRef.current.style.overflowX = 'auto';
    }
    
    if (lockedDirection === 'vertical') {
      if (diffY > 100) {
        // Close threshold reached
        closeLightbox();
      } else {
        // Snap back smoothly
        setSwipeOffsetY(0);
        setLightboxOpacity(1);
      }
    }
    
    // Reset direction lock
    setLockedDirection(null);
    isScrollingHorizontally.current = false;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4">
        <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('albumNotFound')}
        </h1>
        <Link to="/">
          <Button variant="outline">{t('goHome')}</Button>
        </Link>
      </div>
    );
  }

  // Use album.photo_count (includes hidden photos) for limit calculations
  const totalPhotos = album.photo_count || 0;
  const isAlbumFull = album.photo_limit && (album.photo_limit - totalPhotos) <= 0;
  const isAlmostFull = album.photo_limit && (album.photo_limit - totalPhotos) <= Math.ceil(album.photo_limit * 0.2) && !isAlbumFull;
  const remainingPhotos = album.photo_limit ? album.photo_limit - totalPhotos : null;
  
  // Event date restrictions
  const canUpload = album.can_upload !== false; // Allow upload if can_upload is true or undefined
  const isBeforeEvent = album.is_before_event === true;
  const isAfterEvent = album.is_after_event === true;
  const daysRemaining = calculateDaysRemaining(album.expiration_date);
  const isUploadDisabled = isAlbumFull || !canUpload;
  
  // Calculate hidden photos count
  const hiddenPhotosCount = totalPhotos - photos.length;

  return (
    <div className="min-h-screen bg-white" data-testid="upload-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/50 px-4 py-4">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="flex items-center gap-1 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">{t('backToHome') || 'Inicio'}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="minimal" />
            <div className="flex items-center gap-2">
              <img src="/mf-logo.png" alt="MFcreativemanager" className="w-6 h-6 object-contain" />
              <span className="font-semibold text-slate-900 text-sm hidden sm:block" style={{ fontFamily: 'Manrope, sans-serif' }}>
                MFcreativemanager
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-8 max-w-4xl mx-auto w-full">
        
        {/* Mobile View with Cover Image */}
        {isMobile ? (
          <div className="flex flex-col items-center mb-8">
            {/* Cover Image with gradient overlay and title */}
            {album.cover_image_url ? (
              <div className="w-full mb-4 rounded-2xl overflow-hidden relative">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${album.cover_image_url}`}
                  alt={`${album.name} cover`}
                  className="w-full h-64 object-cover"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent" />
                {/* Title over gradient */}
                <div className="absolute bottom-0 left-0 right-0 pb-4 text-center">
                  <h1 
                    className="text-2xl font-bold text-slate-900"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    data-testid="upload-album-title"
                  >
                    {album.name}
                  </h1>
                  <p className="mt-1 text-slate-600">
                    {t('addPhotosToAlbum')}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {totalPhotos}{album.photo_limit ? `/${album.photo_limit}` : ''} {totalPhotos !== 1 ? t('photosUploaded') : t('photoUploaded')}
                    {album.photo_limit && remainingPhotos <= 0 && (
                      <span className="text-red-500 ml-2">• {t('albumFull')}</span>
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="w-full mb-4 h-40 rounded-2xl bg-gradient-to-b from-indigo-100 to-white flex flex-col items-center justify-end pb-4">
                <Camera className="w-10 h-10 text-indigo-300 mb-2" />
                <h1 
                  className="text-2xl font-bold text-slate-900 text-center"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                  data-testid="upload-album-title"
                >
                  {album.name}
                </h1>
                <p className="mt-1 text-slate-600">
                  {t('addPhotosToAlbum')}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {totalPhotos}{album.photo_limit ? `/${album.photo_limit}` : ''} {totalPhotos !== 1 ? t('photosUploaded') : t('photoUploaded')}
                  {album.photo_limit && remainingPhotos <= 0 && (
                    <span className="text-red-500 ml-2">• {t('albumFull')}</span>
                  )}
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Desktop View */
          <div className="text-center mb-8">
            <h1 
              className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="upload-album-title"
            >
              {album.name}
            </h1>
            <p className="text-slate-600">
              {t('addPhotosToAlbum')}
            </p>
            <p className="text-sm text-slate-500 mt-2">
              {totalPhotos}{album.photo_limit ? `/${album.photo_limit}` : ''} {totalPhotos !== 1 ? t('photosUploaded') : t('photoUploaded')}
              {album.photo_limit && remainingPhotos <= 0 && (
                <span className="text-red-500 ml-2">• {t('albumFull')}</span>
              )}
            </p>
          </div>
        )}

        {/* Warnings */}
        {isAlmostFull && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3 max-w-lg mx-auto">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('albumAlmostFull')}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                {t('contactToUpgrade')}
              </p>
            </div>
          </div>
        )}

        {isAlbumFull && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 max-w-lg mx-auto">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('albumFull')}
              </p>
              <p className="text-sm text-red-700 mt-1">
                {t('contactToUpgrade')}
              </p>
            </div>
          </div>
        )}

        {/* Upload Window Closed Warning - only show when event has passed (non-massive events) */}
        {isAfterEvent && !isAlbumFull && !album.is_massive_event && (
          <div className="mb-6 p-4 bg-slate-100 border border-slate-300 rounded-xl flex items-start gap-3 max-w-lg mx-auto">
            <Clock className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('uploadWindowClosed')}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                {t('uploadWindowClosedDesc')}
              </p>
            </div>
          </div>
        )}

        {/* Upload Not Yet Open Warning - show when event is in the future (non-massive events) */}
        {showInfoBanners && isBeforeEvent && !isAlbumFull && !album.is_massive_event && (
          <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl flex items-start gap-3 max-w-lg mx-auto animate-fade-out">
            <Calendar className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-indigo-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('uploadNotYetOpen')}
              </p>
              <p className="text-sm text-indigo-600 mt-1">
                {t('uploadNotYetOpenDesc')} <strong>{formatDate(album.event_date)}</strong>
              </p>
            </div>
          </div>
        )}

        {/* Album Availability Info */}
        {showInfoBanners && album.event_date && !album.is_massive_event && (
          <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl max-w-lg mx-auto animate-fade-out">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              <p className="font-semibold text-indigo-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('albumAvailable3Months')}
              </p>
            </div>
            <div className="text-sm text-indigo-700 space-y-1">
              {album.expiration_date && (
                <p className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {t('albumExpiresOn')} <strong>{formatDate(album.expiration_date)}</strong>
                  {daysRemaining !== null && daysRemaining > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-indigo-200 text-indigo-800 rounded-full text-xs font-semibold">
                      {daysRemaining} {daysRemaining === 1 ? t('dayRemaining') : t('daysRemaining')}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Massive Event Info Section */}
        {album.is_massive_event && (
          <div className="mb-8 max-w-lg mx-auto">
            {/* Event Header Card */}
            <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-2xl p-6 text-white mb-4 shadow-lg">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <Camera className="w-4 h-4" />
                </div>
                <span className="text-sm font-bold uppercase tracking-wider">
                  {album.massive_event_name || album.name}
                </span>
              </div>
              
              {album.massive_event_message && (
                <p className="text-white/90 text-sm mb-4 font-bold">
                  {album.massive_event_message}
                </p>
              )}
              
              {/* Social info */}
              <div className="flex flex-wrap gap-3">
                {album.massive_event_instagram && (
                  <a 
                    href={`https://instagram.com/${album.massive_event_instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-full text-sm hover:bg-white/30 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    {album.massive_event_instagram}
                  </a>
                )}
                {album.massive_event_hashtags && (
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(`#${album.massive_event_hashtags}`);
                      toast.success(t('copiedToClipboard') || '¡Copiado!');
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-full text-sm hover:bg-white/30 transition-colors cursor-pointer"
                  >
                    <span>#</span>
                    {album.massive_event_hashtags}
                    <Copy className="w-3.5 h-3.5 ml-1 opacity-70" />
                  </button>
                )}
              </div>
            </div>

            {/* Date Range Info - Only show when uploads are NOT active */}
            {album.massive_event_start_date && album.massive_event_end_date && !canUpload && (
              <div className={`p-4 rounded-xl flex items-start gap-3 ${
                isBeforeEvent 
                  ? 'bg-amber-50 border border-amber-200' 
                  : 'bg-slate-100 border border-slate-300'
              }`}>
                <Calendar className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  isBeforeEvent ? 'text-amber-500' : 'text-slate-500'
                }`} />
                <div>
                  {isBeforeEvent ? (
                    <>
                      <p className="font-semibold text-amber-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('eventNotStarted')}
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        {t('uploadAvailableFrom')} <strong>{formatDate(album.massive_event_start_date)}</strong>
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('eventEnded')}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        {t('uploadWindowClosedDesc')}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Upload Zone */}
        {!isUploadDisabled && (
          <div className="max-w-lg mx-auto mb-12">
            <div 
              className={`drop-zone min-h-[200px] rounded-3xl flex flex-col items-center justify-center p-8 cursor-pointer transition-all ${isDragging ? 'active' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              
              {uploading ? (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mb-4 mx-auto relative">
                    <svg className="absolute inset-0 w-full h-full -rotate-90">
                      <circle cx="32" cy="32" r="28" stroke="#E2E8F0" strokeWidth="4" fill="none" />
                      <circle
                        cx="32" cy="32" r="28"
                        stroke="#4F46E5" strokeWidth="4" fill="none"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - uploadProgress / 100)}`}
                        strokeLinecap="round"
                        className="transition-all duration-300"
                      />
                    </svg>
                    <span className="text-sm font-bold text-indigo-600">{uploadProgress}%</span>
                  </div>
                  <p className="text-slate-600">{t('uploading')}...</p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-pink-100 flex items-center justify-center mb-4 animate-pulse-subtle">
                    <Upload className="w-8 h-8 text-indigo-600" />
                  </div>
                  <p className="text-base font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {t('tapToAddPhotos')}
                  </p>
                  <p className="text-sm text-slate-500 text-center">
                    {t('dragDropText')}
                  </p>
                </>
              )}
            </div>

            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-button"
              className="w-full mt-4 h-14 rounded-full text-lg font-bold text-white shadow-lg upload-button-glow transition-all hover:scale-105 active:scale-95 bg-gradient-to-r from-indigo-600 to-pink-600"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <div className="spinner" style={{ borderTopColor: 'white' }} />
                  {t('uploading')} {uploadProgress}%
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Camera className="w-6 h-6" />
                  {t('addPhotos')}
                </span>
              )}
            </Button>
          </div>
        )}

        {/* Gallery Section */}
        <div className="border-t border-slate-200 pt-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 
                className="text-xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('albumGallery')} ({totalPhotos})
              </h2>
              {hiddenPhotosCount > 0 && (
                <p className="text-sm text-amber-600 mt-1">
                  {hiddenPhotosCount} {hiddenPhotosCount === 1 ? t('photoHiddenByGuests') : t('photosHiddenByGuests')}
                </p>
              )}
            </div>
            {photos.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadAll}
                className="rounded-full"
                data-testid="download-all-guest"
              >
                <DownloadCloud className="w-4 h-4 mr-2" />
                {t('downloadAllPhotos')}
              </Button>
            )}
          </div>

          {photos.length === 0 ? (
            <div className="text-center py-12">
              {album?.is_massive_event ? (
                /* Empty state for Massive Events - more engaging */
                <div className="max-w-sm mx-auto">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-pink-100 flex items-center justify-center mx-auto mb-6">
                    <Camera className="w-10 h-10 text-indigo-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {t('beFirstToCapture') || '¡Sé el primero en capturar el momento!'}
                  </h3>
                  {album.massive_event_message && (
                    <p className="text-slate-600 mb-4 font-medium">
                      {album.massive_event_message}
                    </p>
                  )}
                  <p className="text-slate-500 text-sm">
                    {t('yourPhotosPrivate') || 'Tus fotos solo las verás tú hasta que el anfitrión las haga públicas'}
                  </p>
                </div>
              ) : (
                /* Empty state for Normal Albums */
                <>
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <ImageIcon className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-medium">{t('noPhotosInGallery')}</p>
                  <p className="text-slate-500 text-sm mt-1">
                    {isBeforeEvent && album.event_date 
                      ? `${t('uploadPhotoOnEventDay')} (${formatDate(album.event_date)})`
                      : t('beFirstToUpload')
                    }
                  </p>
                </>
              )}
            </div>
          ) : isMobile ? (
            /* Mobile: Grid 3 columns */
            <div className="grid grid-cols-3 gap-1">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 animate-fadeIn"
                  style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                  data-testid={`guest-photo-${photo.id}`}
                  onClick={() => openLightbox(photo, index)}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                    alt={photo.original_filename}
                    className="w-full h-full object-cover transition-opacity duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Action buttons - always visible */}
                  <div className="absolute top-1 right-1 flex gap-0.5">
                    <button
                      className="w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPhoto(photo);
                      }}
                      data-testid={`download-guest-${photo.id}`}
                    >
                      <Download className="w-3 h-3" />
                    </button>
                    {/* Hide button - for all albums (guests can only hide, not delete) */}
                    <button
                      className="w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center"
                      onClick={(e) => confirmHidePhoto(photo, e)}
                      data-testid={`hide-guest-${photo.id}`}
                    >
                      <EyeOff className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Desktop: Grid */
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-pointer animate-fadeIn hover:scale-[1.02] transition-transform"
                  style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                  onClick={() => openLightbox(photo, index)}
                  data-testid={`guest-photo-${photo.id}`}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                    alt={photo.original_filename}
                    className="w-full h-full object-cover transition-opacity duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Action buttons - always visible */}
                  <div className="absolute top-1 right-1 flex gap-1">
                    <button
                      className="w-7 h-7 rounded-full bg-black/50 hover:bg-indigo-500 text-white flex items-center justify-center transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPhoto(photo);
                      }}
                      data-testid={`download-guest-${photo.id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    {/* Hide button - for all albums */}
                    <button
                      className="w-7 h-7 rounded-full bg-black/50 hover:bg-amber-500 text-white flex items-center justify-center transition-all"
                      onClick={(e) => confirmHidePhoto(photo, e)}
                      data-testid={`hide-guest-${photo.id}`}
                    >
                      <EyeOff className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Native iOS-style Scroll Snap Lightbox */}
      {lightboxVisible && selectedPhoto && (
        <div 
          className="fixed inset-0 z-50"
          style={{ 
            backgroundColor: `rgba(0, 0, 0, ${lightboxOpacity})`,
            transition: 'background-color 0.2s ease-out',
          }}
          onClick={closeLightbox}
        >
          {/* Close button - top right */}
          <button
            className="absolute top-4 right-4 z-40 w-11 h-11 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-sm"
            style={{ opacity: lightboxOpacity }}
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Photo counter - top left */}
          <div 
            className="absolute top-4 left-4 z-40 bg-black/40 px-4 py-2 rounded-full text-white text-sm font-medium backdrop-blur-sm"
            style={{ opacity: lightboxOpacity }}
          >
            {selectedPhotoIndex + 1} de {photos.length}
          </div>
          
          {/* Navigation arrows - desktop only */}
          {photos.length > 1 && selectedPhotoIndex > 0 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white hidden md:flex items-center justify-center backdrop-blur-sm"
              style={{ opacity: lightboxOpacity }}
              onClick={(e) => {
                e.stopPropagation();
                navigatePhoto(-1);
              }}
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
          )}
          {photos.length > 1 && selectedPhotoIndex < photos.length - 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white hidden md:flex items-center justify-center backdrop-blur-sm"
              style={{ opacity: lightboxOpacity }}
              onClick={(e) => {
                e.stopPropagation();
                navigatePhoto(1);
              }}
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          )}
          
          {/* Native scroll snap carousel with directional locking */}
          <div 
            ref={carouselRef}
            className="absolute inset-0 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory"
            style={{ 
              scrollBehavior: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              zIndex: 10,
              touchAction: lockedDirection === 'vertical' ? 'none' : 'pan-x',
            }}
            onScroll={handleCarouselScroll}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleLightboxTouchStart}
            onTouchMove={handleLightboxTouchMove}
            onTouchEnd={handleLightboxTouchEnd}
          >
            <style>{`
              .hide-scrollbar::-webkit-scrollbar { display: none; }
            `}</style>
            {photos.map((photo, index) => (
              <div 
                key={photo.id}
                className="flex-shrink-0 w-screen h-full flex items-center justify-center snap-center"
                style={{
                  transform: `translateY(${index === selectedPhotoIndex ? swipeOffsetY : 0}px) scale(${index === selectedPhotoIndex ? 1 - swipeOffsetY / 800 : 1})`,
                  transition: lockedDirection === null && swipeOffsetY === 0 ? 'transform 0.2s ease-out' : 'none',
                }}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                  alt={photo.original_filename}
                  className="max-w-[92%] max-h-[70vh] rounded-lg object-contain select-none"
                  style={{
                    boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.4)',
                  }}
                  draggable={false}
                />
              </div>
            ))}
          </div>
          
          {/* Bottom controls */}
          <div 
            className="absolute bottom-0 left-0 right-0 z-40 pb-6 pt-4 pointer-events-none"
            style={{ 
              opacity: lightboxOpacity,
              background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
            }}
          >
            {/* Dot indicators */}
            {photos.length > 1 && photos.length <= 12 && (
              <div className="flex justify-center gap-1.5 mb-4 pointer-events-auto">
                {photos.map((_, idx) => (
                  <button
                    key={idx}
                    className={`h-2 rounded-full transition-all duration-200 ${
                      idx === selectedPhotoIndex 
                        ? 'bg-white w-5' 
                        : 'bg-white/40 w-2'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToPhoto(idx);
                    }}
                  />
                ))}
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex justify-center gap-3 px-4 pointer-events-auto">
              <button
                className="px-5 py-2.5 rounded-full bg-white text-slate-900 font-medium flex items-center gap-2 shadow-lg active:scale-95 transition-transform"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadPhoto(selectedPhoto);
                }}
              >
                <Download className="w-4 h-4" />
                {t('download')}
              </button>
              {/* Hide button - for all albums (guests can only hide, not delete) */}
              <button
                className="px-5 py-2.5 rounded-full bg-white/20 text-white font-medium flex items-center gap-2 backdrop-blur-sm active:scale-95 transition-transform"
                onClick={(e) => confirmHidePhoto(selectedPhoto, e)}
              >
                <EyeOff className="w-4 h-4" />
                {t('hidePhoto')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide Photo Confirmation Modal */}
      {showHideConfirm && photoToHide && (
        <div 
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
          onClick={() => {
            setShowHideConfirm(false);
            setPhotoToHide(null);
          }}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <EyeOff className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('confirmHidePhoto') || '¿Ocultar esta foto?'}
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                {t('hidePhotoWarning') || 'La foto se ocultará de la galería pública, pero el anfitrión aún podrá verla y solo él puede eliminarla permanentemente.'}
              </p>
              
              {/* Photo preview */}
              <div className="w-24 h-24 mx-auto mb-4 rounded-lg overflow-hidden">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${photoToHide.url}`}
                  alt={photoToHide.original_filename}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => {
                    setShowHideConfirm(false);
                    setPhotoToHide(null);
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  className="flex-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => handleHidePhoto(photoToHide.id)}
                >
                  <EyeOff className="w-4 h-4 mr-2" />
                  {t('hidePhoto')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instagram Modal - Only for massive events */}
      {showInstagramModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={handleInstagramModalCancel}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 flex items-center justify-center mx-auto mb-4">
                <Instagram className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('shareYourInstagram') || '¿Cuál es tu Instagram?'}
              </h3>
              <p className="text-sm text-slate-600">
                {t('instagramMentionHint') || 'Compártenos tu Instagram para mencionarte en las publicaciones oficiales'}
              </p>
            </div>
            
            <div className="mb-6">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">@</span>
                <input
                  type="text"
                  value={uploaderInstagram}
                  onChange={(e) => handleInstagramChange(e.target.value)}
                  placeholder={t('instagramPlaceholder') || 'tu_usuario'}
                  className="w-full h-12 pl-10 pr-4 rounded-xl border-2 border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  data-testid="uploader-instagram-modal-input"
                  autoFocus
                />
              </div>
              <p className="text-xs text-slate-400 mt-2 text-center">
                {t('optionalField') || 'Este campo es opcional'}
              </p>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button
                className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-pink-600 text-white font-semibold"
                onClick={handleInstagramModalSubmit}
                data-testid="instagram-modal-submit"
              >
                {t('continueUpload') || 'Continuar y subir fotos'}
                <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
                  {pendingFiles.length}
                </span>
              </Button>
              <button
                className="text-sm text-slate-500 hover:text-slate-700 py-2"
                onClick={handleInstagramModalCancel}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
